import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Sun, Moon, List, X, CaretDown, Lock, SignOut, Gear } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { NotificationBell } from "@/components/NotificationBell";
import { MobileMenuDrawer } from "@/components/header/MobileMenuDrawer";
import { BusinessSwitcher } from "@/components/header/BusinessSwitcher";
import { UsageBadge } from "@/components/header/UsageBadge";
import { PRIMARY_NAV, SECONDARY_NAV, isNavLocked, GATED_FEATURES } from "@/components/header/headerNav";

export const Header = () => {
  const { user, api, logout, activeBusiness, businesses, switchBusiness, openUpgradeModal } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [usage, setUsage] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bizSwitcherOpen, setBizSwitcherOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const bizRef = useRef(null);
  const moreRef = useRef(null);

  const tier = usage?.tier || "free_beta";
  const isAdmin = usage?.is_admin;
  const isLocked = (path) => isNavLocked(path, tier, isAdmin);

  useEffect(() => {
    setMobileMenuOpen(false);
    setBizSwitcherOpen(false);
    setMoreMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e) => {
      if (bizRef.current && !bizRef.current.contains(e.target)) setBizSwitcherOpen(false);
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const resp = await api.get("/usage");
        setUsage(resp.data);
      } catch { /* silent */ }
    };
    fetchUsage();
  }, [api, location.pathname]);

  // Don't render header for client users
  if (user?.role === "client") return null;

  return (
    <>
      <header className="sticky top-0 z-50 bg-[var(--bg-base)]/80 backdrop-blur-xl border-b border-[var(--border-strong)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="header-logo">
              <img src="/web_logo.svg" alt="Quibn" className="h-8 w-auto" data-testid="header-logo" />
            </Link>

            <BusinessSwitcher
              businesses={businesses}
              activeBusiness={activeBusiness}
              switchBusiness={switchBusiness}
              open={bizSwitcherOpen}
              onToggle={() => setBizSwitcherOpen(!bizSwitcherOpen)}
              onClose={() => setBizSwitcherOpen(false)}
              innerRef={bizRef}
            />
          </div>

          <nav className="hidden md:flex items-center gap-0.5">
            {PRIMARY_NAV.map(({ path, label, icon: Icon }) => {
              const locked = isLocked(path);
              if (locked) {
                return (
                  <button
                    key={path}
                    onClick={() => {
                      const feature = GATED_FEATURES[path];
                      openUpgradeModal({
                        error: "feature_not_available",
                        message: "This feature is not available on your current plan.",
                        feature,
                        tier,
                        upgrade_required: true,
                      });
                    }}
                    className="px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] opacity-60"
                    data-testid={`nav-${label.toLowerCase().replace(' ', '-')}-locked`}
                  >
                    <Icon size={16} />
                    <span className="hidden lg:inline">{label}</span>
                    <Lock size={10} weight="fill" className="text-[var(--text-muted)]" />
                  </button>
                );
              }
              return (
                <Link
                  key={path}
                  to={path}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 ${
                    location.pathname === path
                      ? "text-[#FF4400] bg-[var(--accent-subtle)]"
                      : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
                  }`}
                  data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon size={16} weight={location.pathname === path ? "fill" : "regular"} />
                  <span className="hidden lg:inline">{label}</span>
                </Link>
              );
            })}

            {/* More dropdown for secondary nav */}
            {SECONDARY_NAV.length > 0 && (
              <div className="relative" ref={moreRef}>
                <button
                  onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 ${
                    SECONDARY_NAV.some(n => location.pathname === n.path)
                      ? "text-[#FF4400] bg-[var(--accent-subtle)]"
                      : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
                  }`}
                  data-testid="nav-more-btn"
                >
                  <List size={16} />
                  <span className="hidden lg:inline">More</span>
                  <CaretDown size={10} className="text-[var(--text-muted)]" />
                </button>
                <AnimatePresence>
                  {moreMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="absolute top-full right-0 mt-1 w-52 bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-lg shadow-lg py-1 z-50"
                      data-testid="more-menu-dropdown"
                    >
                      {SECONDARY_NAV.map(({ path, label, icon: Icon }) => (
                        <Link
                          key={path}
                          to={path}
                          onClick={() => setMoreMenuOpen(false)}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 transition-colors ${
                            location.pathname === path
                              ? "text-[#FF4400] bg-[var(--accent-subtle)]"
                              : "text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
                          }`}
                          data-testid={`nav-more-${label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <Icon size={16} weight={location.pathname === path ? "fill" : "regular"} />
                          {label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <UsageBadge usage={usage} isAdmin={isAdmin} />

            <NotificationBell />

            <button onClick={toggleTheme} className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" data-testid="theme-toggle">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <div className="hidden sm:flex items-center gap-2">
              <span className="text-xs text-[var(--text-secondary)] hidden lg:block truncate max-w-[140px]">{user?.email}</span>
              <Link
                to="/settings"
                className={`p-2 rounded-md transition-colors duration-200 ${
                  location.pathname.startsWith("/settings")
                    ? "text-[#FF4400] bg-[var(--accent-subtle)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
                }`}
                data-testid="header-settings-link"
                aria-label="Settings"
              >
                <Gear size={18} />
              </Link>
              <button
                onClick={logout}
                className="p-2 rounded-md text-[var(--text-muted)] hover:text-[#FF4400] hover:bg-[var(--accent-subtle)] transition-colors duration-200"
                data-testid="logout-button"
              >
                <SignOut size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile bottom nav - 4 key items + More */}
        <div className="md:hidden flex items-center border-t border-[var(--border)]">
          {PRIMARY_NAV.slice(0, 4).map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors ${
                location.pathname === path ? "text-[#FF4400]" : "text-[var(--text-muted)]"
              }`}
              data-testid={`mobile-nav-${label.toLowerCase()}`}
            >
              <Icon size={22} weight={location.pathname === path ? "fill" : "regular"} />
              {label}
            </Link>
          ))}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors ${
              mobileMenuOpen || SECONDARY_NAV.some(n => n.path === location.pathname)
                ? "text-[#FF4400]"
                : "text-[var(--text-muted)]"
            }`}
            data-testid="mobile-nav-more"
          >
            {mobileMenuOpen ? <X size={22} /> : <List size={22} />}
            More
          </button>
        </div>
      </header>

      <MobileMenuDrawer
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        logout={logout}
      />
    </>
  );
};
